<template>
    <div class="forum-user">
        <div
            class="panel"
            v-if="user">

            <div class="panel-heading">
                <h2>{{ user.firstName }} {{ user.lastName }}</h2>
            </div>

            <div class="panel-body">
                <div class="well clearfix">

                    <div class="col-xs-12 col-md-6">
                        <img :src="user.avatar">
                    </div>

                    <div class="col-xs-12 col-md-6">
                        <h3>{{ user.firstName }} {{ user.lastName }}</h3>

                        <h4 v-if="user.title">
                            {{ user.title }}
                        </h4>

                        <!-- This doesn't appear to be used? No user has them.
                        <ul
                            class="social-links"
                            v-if="userSocialLinks">
                            <li v-for="(value, propertyName) in posts[0].poster.socialLinks">
                                <a :href="value" target="_blank">
                                    {{ propertyName }}
                                </a>
                            </li>
                        </ul>
                        -->
                    </div>
                </div>
            </div>
        </div>

        <div
            class="panel posts-panel"
            v-show="posts.length > 0">

            <div class="panel-heading">
                <h2>Recent Posts</h2>
            </div>

            <div class="panel-body">
                <ul
                    class="post-list"
                    ref="postList">

                    <li
                        :key="post.threadId + '_' + post.id"
                        v-for="post in posts">
                        <ForumPostResult
                            :post="post"
                            :thread="post.thread">
                            <template slot="post-content">
                                <div
                                    class="post-content-html"
                                    v-html="post.content">
                                </div>
                            </template>
                        </ForumPostResult>
                    </li>
                </ul>

                <div
                    class="after-posts-msg"
                    v-if="!postList.hasMore()">
                    That's all, folks!
                </div>
            </div>
        </div>
    </div>
</template>


<script>
    // ForumUser: the profile page for a forum user. Displays their name,
    // profile picture, and a history of their posts.

    import UserService from '../services/users';
    import ForumService from '../services/forum';
    import ForumUtils from '../utils/forum';
    import ForumPostResult from './reusable/forum/post-result';

    // The "ResourceLoader" pattern is one that I've started using quite a bit
    // to avoid race conditions when it comes time to reload. The loader
    // instance will wrap the actual resource, which allows you to reload the
    // resource as often as you want without worrying about an old response
    // getting delayed and then overwriting a newer one.
    //
    // The core idea of this that seems to work quite well is to wrap
    // asynchronous resourcess with a synchronous interfaces.
    class UserLoader {
        constructor(userId) {
            this.user = null;
            this.error = null;

            if (userId) {
                UserService.getUser(userId).then(
                    user => this.user = user,
                    error => this.error = error,
                );
            }
        }
    }

    /**
     * The paginated post list from the forum utils uses getPostsAndCount(), but
     * we need to get posts using searchPosts().
     */
    class PaginatedAuthorPostList extends ForumUtils.PaginatedList {
        constructor(authorId) {
            const fetchFn = async (limit, offset) => {
                const res = await ForumService.searchPosts('', {
                    limit,
                    offset,
                    authorId,
                    order: 'createdAt:desc',
                });
                return {
                    items: res.posts,
                    count: res.count,
                };
            };
            super(fetchFn);
        }

        get posts() {
            return this.items;
        }
    }

    function getViewportBottom() {
        const $window = $(window);
        return $window.scrollTop() + $window.height();
    }

    function getBottomPos(elem) {
        const $elem = $(elem);
        return $elem.offset().top + $elem.outerHeight();
    }

    export default {
        components: {
            ForumPostResult,
        },

        data() {
            return {
                userLoader: new UserLoader(),
                postList: new PaginatedAuthorPostList(),
            };
        },

        computed: {
            userId() {
                return this.$route.params.userId;
            },

            user() {
                return this.userLoader.user;
            },

            posts() {
                return this.postList.posts;
            },

            /*
            userSocialLinks() {
                const json = this.user.socialLinks
                    .replace(/\\/g,'')
                    .substr(1)
                    .slice(0, -1);
                return JSON.parse(json);
            },
            */
        },

        created() {
            this.reloadUser();

            // Reload the posts rather than auto loading since the original list
            // is created without search parameters (ie. the user id).
            this.reloadPostList();
        },

        mounted() {
            window.addEventListener('scroll', this.onScroll);
            window.addEventListener('resize', this.onResize);
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.onScroll);
            window.removeEventListener('resize', this.onResize);
        },

        methods: {
            onScroll() {
                this.autoLoadPosts();
            },

            onResize() {
                this.autoLoadPosts();
            },

            async reloadUser() {
                this.userLoader = new UserLoader(this.userId);
            },

            async reloadPostList() {
                this.postList = new PaginatedAuthorPostList(this.userId);
                await this.autoLoadPosts();
            },

            isPostListBottomNearView() {
                if (!this.$refs.postList) {
                    return false
                }
                const offset = 200;
                const viewBottom = getViewportBottom();
                const listBottom = getBottomPos(this.$refs.postList);
                return listBottom <= viewBottom + offset;
            },

            shouldLoadMorePosts() {
                if (!this.postList.hasMore()) {
                    // There is nothing else to load.
                    return false;

                } else if (this.postList.isLoading()) {
                    // We are already loading the next batch.
                    return false;

                } else if (this.posts.length === 0) {
                    // If the post list was just reloaded, the bottom may still
                    // be out of view due to the old rendered posts; in this
                    // case, we want to fetch the new ones.
                    return true;
                }
                return this.isPostListBottomNearView();
            },

            async autoLoadPosts() {
                while (this.shouldLoadMorePosts()) {
                    await this.postList.loadMore();
                }
            },
        },

        watch: {
            userId() {
                this.user = null;
                this.posts = null;
                this.reloadUser();
                this.reloadPostList();
            },
        }
    }
</script>


<style lang="scss" scoped>
    .posts-panel {
        max-width: calc(780px + 20px * 2);
        margin-left: auto;
        margin-right: auto;

        .panel-body {
            padding: 0 20px 30px;
        }
    }

    ul.post-list {
        border-bottom: 1px solid #333;
    }

    li {
        list-style: none;

        &:first-of-type {
            .forum-post-result {
                border-top: none;
                margin-top: 7px;
            }
        }

        .forum-post-result {
            border-top: 1px solid #333;
            margin-left: 0;
            margin-right: 0;
        }
    }

    .after-posts-msg {
        font-weight: bold;
        margin-top: 20px;
    }
</style>



// WEBPACK FOOTER //
// ForumUser.vue?74d0cd84